import { useEffect, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Grid3x3, Mic, PhoneCall, PhoneOff, Signal, Timer, Users, Volume2 } from "lucide-react";

interface PresenceAgent {
  code: string;
  avatar: string;
}

const HERO_PHONE_STYLES = `
@keyframes hero-wave {
  0%, 100% { transform: scaleY(0.55); }
  50% { transform: scaleY(0.92); }
}
.hero-wave-bar { transform-origin: center; animation: hero-wave ease-in-out infinite; will-change: transform; }
@keyframes hero-call-ring {
  0% { transform: scale(1); opacity: 0.26; }
  100% { transform: scale(1.55); opacity: 0; }
}
.hero-call-ring { animation: hero-call-ring 3s ease-out infinite; }
`;

// 声纹波形：固定参数，保证 SSR/CSR 一致
const WAVE_BARS = Array.from({ length: 24 }, (_, i) => {
  const center = Math.abs(i - 11.5) / 11.5; // 0(中心)→1(两端)
  const base = 18 - center * 8; // 中间略高，两端收敛
  const duration = 1.35 + ((i * 37) % 45) / 100; // 1.35 ~ 1.79s
  const delay = ((i * 53) % 120) / 100; // 0 ~ 1.19s
  return { height: Math.round(base), duration, delay };
});

/** 圆形头像：把全身技师照裁切到面部 */
function FaceAvatar({
  src,
  alt = "",
  className = "",
  lazy = true,
}: {
  src: string;
  alt?: string;
  className?: string;
  lazy?: boolean;
}) {
  return (
    <div className={`overflow-hidden rounded-full bg-[#0C2A45] ${className}`}>
      <img
        src={src}
        alt={alt}
        loading={lazy ? "lazy" : undefined}
        decoding="async"
        draggable={false}
        className="h-full w-full scale-[1.35] object-cover object-top"
        onError={(e) => {
          (e.target as HTMLImageElement).style.opacity = "0";
        }}
      />
    </div>
  );
}

function formatTime(total: number) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function HeroVisualPhone() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const presence = (t("home.heroPhone.presence", { returnObjects: true }) as PresenceAgent[]) ?? [];

  // 通话计时器
  const [seconds, setSeconds] = useState(134);
  useEffect(() => {
    if (reduceMotion) return;
    const timer = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(timer);
  }, [reduceMotion]);

  // 排队等待数字轻微波动
  const [queue, setQueue] = useState(9);
  useEffect(() => {
    if (reduceMotion) return;
    const timer = window.setInterval(() => {
      setQueue(() => Math.max(4, Math.min(18, 4 + Math.floor(Math.random() * 14))));
    }, 2600);
    return () => window.clearInterval(timer);
  }, [reduceMotion]);

  return (
    <div className="relative flex h-full min-h-[460px] w-full items-center justify-center overflow-visible">
      <style>{HERO_PHONE_STYLES}</style>

      {/* 背景氛围 */}
      <div
        className="pointer-events-none absolute right-[6%] top-[8%] h-[300px] w-[300px] rounded-full bg-[#00D4AA]/8 blur-[90px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-[10%] bottom-[10%] h-[220px] w-[220px] rounded-full bg-[#1E4FB3]/10 blur-[80px]"
        aria-hidden
      />

      <div className="relative w-[320px] pb-10 sm:w-[368px] lg:w-[404px]">
        {/* 背景装饰同心圆 */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2"
          aria-hidden
        >
          <div className="h-[300px] w-[300px] rounded-full border border-white/6 sm:h-[380px] sm:w-[380px]" />
          <div className="absolute left-1/2 top-1/2 h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/5 sm:h-[540px] sm:w-[540px]" />
        </div>

        {/* 主体：通话工作台卡片 */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10"
        >
          <motion.div
            animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            style={{
              willChange: "transform",
              backfaceVisibility: "hidden",
              WebkitFontSmoothing: "antialiased",
              transform: "translateZ(0)",
            }}
            className="overflow-hidden rounded-2xl border border-white/12 bg-linear-to-b from-[#0D2E4E]/95 to-[#0A2540]/95 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          >
            {/* 窗口头部 */}
            <div className="flex items-center gap-3 border-b border-white/8 px-5 py-3.5">
              <div className="flex items-center gap-1.5" aria-hidden>
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-300">
                <PhoneCall className="h-3.5 w-3.5 text-[#00D4AA]" strokeWidth={2} aria-hidden />
                {t("home.heroPhone.title")}
              </div>
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#00D4AA]/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#3FE0BE]">
                <span className="relative flex h-1.5 w-1.5" aria-hidden>
                  {!reduceMotion && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00D4AA] opacity-60" />
                  )}
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00D4AA]" />
                </span>
                {t("home.heroPhone.live")}
              </span>
            </div>

            {/* 通话主区：头像 + 通话状态 + 计时 */}
            <div className="flex flex-col items-center px-5 pt-6 pb-4">
              <div className="relative">
                {!reduceMotion && (
                  <>
                    <span className="hero-call-ring absolute inset-0 rounded-full border border-[#00D4AA]/35" aria-hidden />
                    <span
                      className="hero-call-ring absolute inset-0 rounded-full border border-[#00D4AA]/35"
                      style={{ animationDelay: "1.5s" }}
                      aria-hidden
                    />
                  </>
                )}
                <FaceAvatar
                  src="/images/agent_us.png"
                  alt={t("home.heroPhone.agentName") as string}
                  lazy={false}
                  className="h-16 w-16 border-2 border-[#00D4AA]/50 shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
                />
                <span className="absolute bottom-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-[#0D2E4E] bg-[#00D4AA]">
                  <PhoneCall className="h-2.5 w-2.5 text-[#06243C]" strokeWidth={2.5} aria-hidden />
                </span>
              </div>

              <div className="mt-3 text-[14px] font-semibold text-white">
                {t("home.heroPhone.agentName")}
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
                <span className="text-[#3FE0BE]">{t("home.heroPhone.callStatus")}</span>
                <span className="text-slate-600">·</span>
                <span className="tabular-nums text-slate-200">{formatTime(seconds)}</span>
              </div>

              {/* 来电方 */}
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/4 px-3 py-1.5">
                <Signal className="h-3 w-3 text-[#00D4AA]" strokeWidth={2} aria-hidden />
                <span className="text-[10px] uppercase tracking-wide text-slate-500">
                  {t("home.heroPhone.callerLabel")}
                </span>
                <span className="text-[11px] font-medium text-slate-200">
                  {t("home.heroPhone.callerRegion")}
                </span>
              </div>
            </div>

            {/* 声纹波形 */}
            <div className="flex h-8 items-center justify-center gap-1 px-5" aria-hidden>
              {WAVE_BARS.map((bar, i) => (
                <span
                  key={i}
                  className="hero-wave-bar w-0.5 rounded-full bg-linear-to-t from-[#00D4AA]/20 to-[#8BF5DE]/65"
                  style={{
                    height: `${bar.height}px`,
                    animationDuration: reduceMotion ? "0s" : `${bar.duration}s`,
                    animationDelay: reduceMotion ? "0s" : `${bar.delay}s`,
                    transform: reduceMotion ? "scaleY(0.7)" : undefined,
                  }}
                />
              ))}
            </div>

            {/* 通话控制 */}
            <div className="flex items-center justify-center gap-3 px-5 py-4">
              <CallButton label={t("home.heroPhone.controlMute")}>
                <Mic className="h-4 w-4" strokeWidth={2} />
              </CallButton>
              <CallButton label={t("home.heroPhone.controlKeypad")}>
                <Grid3x3 className="h-4 w-4" strokeWidth={2} />
              </CallButton>
              <CallButton label={t("home.heroPhone.controlSpeaker")}>
                <Volume2 className="h-4 w-4" strokeWidth={2} />
              </CallButton>
              <CallButton label={t("home.heroPhone.controlEnd")} danger>
                <PhoneOff className="h-4 w-4" strokeWidth={2.2} />
              </CallButton>
            </div>

            {/* 指标条 */}
            <div className="grid grid-cols-3 gap-px border-t border-white/8 bg-white/4">
              <Metric
                icon={<PhoneCall className="h-3.5 w-3.5" strokeWidth={2} />}
                value={t("home.heroPhone.statAnswerValue")}
                label={t("home.heroPhone.statAnswer")}
              />
              <Metric
                icon={<Timer className="h-3.5 w-3.5" strokeWidth={2} />}
                value={t("home.heroPhone.statDurationValue")}
                label={t("home.heroPhone.statDuration")}
              />
              <Metric
                icon={<Users className="h-3.5 w-3.5" strokeWidth={2} />}
                value={t("home.heroPhone.statConcurrentValue")}
                label={t("home.heroPhone.statConcurrent")}
              />
            </div>
          </motion.div>
        </motion.div>

        {/* 浮动卡片：排队等待 */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 14, x: 14 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="hidden absolute -right-3 -top-5 z-20 sm:-right-6"
        >
          <motion.div
            animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            style={{
              willChange: "transform",
              backfaceVisibility: "hidden",
              WebkitFontSmoothing: "antialiased",
              transform: "translateZ(0)",
            }}
            className="rounded-xl border border-white/12 bg-[#0A2540]/85 px-4 py-3 shadow-[0_14px_36px_-12px_rgba(0,0,0,0.55)] backdrop-blur-md"
          >
            <div className="flex items-baseline gap-1 text-[20px] font-bold leading-none tabular-nums tracking-tight text-white">
              {queue}
              <span className="text-[12px] font-medium text-slate-400">s</span>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <span className="relative flex h-1.5 w-1.5" aria-hidden>
                {!reduceMotion && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00D4AA] opacity-50" />
                )}
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#00D4AA]" />
              </span>
              <span className="text-[11px] text-slate-400">{t("home.heroPhone.chipQueueLabel")}</span>
            </div>
          </motion.div>
        </motion.div>

        {/* 浮动卡片：在线话务头像组 */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 14, x: -14 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-0 -left-3 z-20 sm:-left-7"
        >
          <motion.div
            animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
            transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
            style={{
              willChange: "transform",
              backfaceVisibility: "hidden",
              WebkitFontSmoothing: "antialiased",
              transform: "translateZ(0)",
            }}
            className="flex items-center gap-3 rounded-xl border border-white/12 bg-[#0A2540]/85 px-3.5 py-2.5 shadow-[0_14px_36px_-12px_rgba(0,0,0,0.55)] backdrop-blur-md"
          >
            <div className="flex -space-x-2.5">
              {presence.map((agent) => (
                <FaceAvatar
                  key={agent.code}
                  src={agent.avatar}
                  className="h-7 w-7 border-2 border-[#0A2540]"
                />
              ))}
            </div>
            <div className="leading-tight">
              <div className="text-[14px] font-bold tabular-nums text-white">
                {t("home.heroPhone.stackValue")}
              </div>
              <div className="text-[10px] text-slate-400">{t("home.heroPhone.stackLabel")}</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

function CallButton({
  children,
  label,
  danger = false,
}: {
  children: ReactNode;
  label: string;
  danger?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
          danger
            ? "border-transparent bg-[#FF5F57] text-white shadow-[0_8px_20px_-8px_rgba(255,95,87,0.7)]"
            : "border-white/12 bg-white/8 text-slate-200"
        }`}
      >
        {children}
      </div>
      <span className="text-[9px] text-slate-500">{label}</span>
    </div>
  );
}

function Metric({
  icon,
  value,
  label,
}: {
  icon: ReactNode;
  value: ReactNode;
  label: ReactNode;
}) {
  return (
    <div className="bg-[#0A2540]/40 px-3 py-3 text-center">
      <div className="mb-1 flex justify-center text-[#00D4AA]">{icon}</div>
      <div className="text-[14px] font-bold leading-none tabular-nums text-white">{value}</div>
      <div className="mt-1 text-[10px] leading-tight text-slate-400">{label}</div>
    </div>
  );
}
